import {
  formatShortDateAndTimeOrSameDay,
  formatShortDateOrSameDay,
} from '@gripp/shared-logic';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { ActivityCard } from '../crop-circle/Components/ActivityCard';

/**
 * Server-side aggregated row shape returned by
 * `campaign.campaignAggregatedActivities` (see PR 4 A1, gripp-eav).
 *
 * One row per campaign — assetCount/replyCount/latestActivityAt are
 * pre-aggregated server-side; the card is a pure renderer.
 */
export interface CampaignAggregatedActivityData {
  campaignId: string;
  title: string;
  /** OEM workspace brand name — used as the attribution pill (e.g. "John Deere"). */
  oemWorkspaceBrand?: string | null;
  /** Number of targeted assets in the campaign (across all viewing workspaces). */
  assetCount: number;
  /** Number of replies across child Issues (excludes each Issue's dispatch seed). */
  replyCount: number;
  /** ISO timestamp of the most recent message on any child Issue. */
  latestActivityAt: string;
  /** Thumbnail URL of the first media attached to the campaign; null if no media. */
  firstMediaThumbnailUrl?: string | null;
  /** Plain-text preview of the latest message; null/empty when no messages yet. */
  latestMessagePreview?: string | null;
}

export interface CampaignAggregatedCardProps {
  activity: CampaignAggregatedActivityData;
  /** Whether to show the brand-attribution pill (default: true). */
  showAttribution?: boolean;
  /** Whether to show the campaign thumbnail (default: true). */
  showImage?: boolean;
  isUnread?: boolean;
  isSelected?: boolean;
  borderRadius?: number;
}

/**
 * Feed row for an aggregated campaign activity. Wraps the Crop Circle
 * `ActivityCard` primitive with campaign-specific data shaping:
 *
 * - Pink Type variant (`'campaign'`, #D649A6) — matches `IssueCard` when
 *   `Issue.type === CAMPAIGN` (see PR 4 C1, commit ec19837f0).
 * - OEM workspace brand rendered in the attribution pill slot
 *   (where IssueCard renders the asset name).
 * - First-image thumbnail with graceful fallback when null/missing
 *   (ActivityCard renders a QR placeholder via its `imageUrl` undefined path).
 * - Latest-message preview as the description row.
 * - Asset count + reply count rendered as supporting metadata via the
 *   `labels` slot (renders as StatusTag pills below the description),
 *   matching the visual weight of IssueCard's label row.
 *
 * Layout parity with `IssueCard` is delegated to the shared `ActivityCard`
 * primitive — same padding, heights, divider, and row spacing.
 */
export const CampaignAggregatedCard = (
  props: CampaignAggregatedCardProps
) => {
  const { t } = useTranslation();
  const { activity } = props;

  const isWeb = Platform.OS === 'web';
  const formattedDate = activity.latestActivityAt
    ? isWeb
      ? formatShortDateAndTimeOrSameDay(activity.latestActivityAt)
      : formatShortDateOrSameDay(activity.latestActivityAt)
    : '';

  const thumbnailUrl =
    activity.firstMediaThumbnailUrl &&
    `${activity.firstMediaThumbnailUrl}`.trim().length > 0
      ? activity.firstMediaThumbnailUrl
      : undefined;

  const description = activity.latestMessagePreview?.trim() || undefined;

  const attribution = activity.oemWorkspaceBrand?.trim();
  const showAttribution =
    (props.showAttribution ?? true) && !!attribution && attribution.length > 0;

  // Render assetCount / replyCount as StatusTag pills via `labels`. The
  // ActivityCard's overflow handling will collapse them gracefully on
  // narrow screens. Stable ids let React reconcile without churn.
  const metadataLabels: { id: string; name: string }[] = [
    {
      id: `campaign-${activity.campaignId}-assets`,
      name: t('campaigns.aggregated.assetCount', {
        count: activity.assetCount,
      }),
    },
    {
      id: `campaign-${activity.campaignId}-replies`,
      name: t('campaigns.aggregated.replyCount', {
        count: activity.replyCount,
      }),
    },
  ];

  return (
    <ActivityCard
      // OEM brand sits in the same pill slot IssueCard uses for asset name —
      // this is the per-row attribution surface. Falls back to empty string
      // when the brand is missing; the pill is hidden via showAssetName.
      assetName={attribution ?? ''}
      showAssetName={showAttribution}
      imageUrl={thumbnailUrl}
      showImage={props.showImage}
      title={activity.title}
      date={formattedDate}
      description={description}
      labels={metadataLabels}
      severityVariant="campaign"
      isUnread={props.isUnread}
      isSelected={props.isSelected}
      borderRadius={props.borderRadius}
    />
  );
};

CampaignAggregatedCard.displayName = 'CampaignAggregatedCard';
